class CBH {

    /**
     * Calculates a point on a Bezier curve.
     * @param {number} t - The parameter t, where 0 <= t <= 1.
     * @param {Object} p0 - The first control point.
     * @param {Object} p1 - The second control point.
     * @param {Object} p2 - The third control point.
     * @param {Object} p3 - The fourth control point.
     * @returns {Object} The calculated point on the Bezier curve.
     */
    static calculateBezierPoint(t, p0, p1, p2, p3) {
        const u = 1 - t;
        const uu = u * u;
        const uuu = uu * u;
        const tt = t * t;
        const ttt = tt * t;

        const x = uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x;
        const y = uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y;

        return { x, y };
    }

    static findTForX(x, p0, p1, p2, p3, precision = 0.000001) {
        let tMin = 0;
        let tMax = 1;
        let t;

        while (tMax - tMin > precision) {
            t = (tMin + tMax) / 2;
            const point = CBH.calculateBezierPoint(t, p0, p1, p2, p3);

            if (point.x < x) {
                tMin = t;
            } else {
                tMax = t;
            }
        }
        return t;
    }

    static interpolateBezier(x, p0, p1, p2, p3, precision) {
        const t = CBH.findTForX(x, p0, p1, p2, p3, precision);
        return CBH.calculateBezierPoint(t, p0, p1, p2, p3);
    }

    static linearSteps(firstStep = 0, lastStep = 100, stepSize = 1) {
        const steps = (lastStep - firstStep) / stepSize + 1;
        return Array.from({ length: steps }, (_, i) => firstStep + (i * stepSize));
    }

    static defaultBeziers(width = 100, height = 100, length = 0.4) {

        const short = length;
        const large = 1 - length;

        return [
            { cpt: { x: 0, y: height }, bpt: { x: width * short, y: height * large } },
            { cpt: { x: width, y: 0  }, bpt: { x: width * large, y: height * short } }
        ];
    }

    static flattenBeziers(cpt1, cpt2, length = 0.4) {

        const short = length;
        const large = 1 - length;
    
        const ironedBpt1 = {
            x: cpt1.x + (cpt2.x - cpt1.x) * short,
            y: cpt1.y + (cpt2.y - cpt1.y) * short
        };
    
        const ironedBpt2 = {
            x: cpt1.x + (cpt2.x - cpt1.x) * large,
            y: cpt1.y + (cpt2.y - cpt1.y) * large
        };
    
        return [
            { cpt: cpt1, bpt: ironedBpt1 },
            { cpt: cpt2, bpt: ironedBpt2 }
        ];
    }

    static easeBeziers(cpt1, cpt2, length = 0.4) {

        const short = length;
        const large = 1 - length;

        const width = cpt2.x - cpt1.x;

        const ptsNew = [
            { cpt: cpt1, bpt: { x: width * short, y: cpt1.y } },
            { cpt: cpt2, bpt: { x: width * large, y: cpt2.y } }
        ];

        return ptsNew;
    }
}

export{
    CBH
};